import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Briefcase, Building2, ChevronLeft, Eye, EyeOff, Store, UserCircle2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { authApi } from '@/services/auth'
import { buildSignupEmailVerificationPath, isSignupEmailOtpVerificationContext } from '@/lib/auth-verification'
import { orgTreeApi, type OrgTreeServiceInstance } from '@/services/org-tree'
import { buildPostSelectionPath } from '@/lib/auth-redirect'
import { useSelectedBackendServerUrl } from '@/lib/backend-servers'
import { getErrorMessage } from '@/lib/api'
import { AuthTermsDisclosure } from '@/components/auth/auth-terms-disclosure'
import { BackendServerSelector } from '@/components/auth/backend-server-selector'
import { useRefetchOnReconnect } from '@/hooks/use-refetch-on-reconnect'
import { buildAuthThemeStyle, getAuthThemeServiceInstanceId, useAuthTheme } from '@/lib/auth-theme'
import { resolveServiceInstanceAuthLogos } from '@/lib/auth-branding'
import { TenantAuthShell } from '@/components/auth/tenant-auth-shell'
import { isRootOrgServiceInstance } from '@/lib/org-hierarchy-from-instance'

const PHONE_NUMBER_SAMPLE_PLACEHOLDER = '9876543210'

type PublicSignupFormData = {
  first_name: string
  last_name: string
  email: string
  username: string
  phone_number: string
  country_id: string
  state_id: string
  date_of_birth: string
  age: string
  gender: string
  password: string
  password_confirm: string
  acknowledge_exposure_notice: boolean
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
]

const resolveOrganizationName = (instance: OrgTreeServiceInstance | null | undefined) =>
  instance?.name || instance?.title || instance?.identifier || instance?.url_identifier || 'Organization'

const resolveServiceInstanceReference = (instance: OrgTreeServiceInstance | null | undefined, routeReference: string | undefined) =>
  instance?.instance_id || routeReference || (instance ? String(instance.id) : '')

const normalizePhoneForCountry = (
  rawPhone: string,
  country: { phone_number_mask?: string | null; dial_code?: string | null } | null | undefined,
) => {
  const mask = String(country?.phone_number_mask ?? '').trim()
  if (!mask || !mask.includes('#')) {
    return null
  }

  const expectedLength = (mask.match(/#/g) ?? []).length
  const digitsOnly = String(rawPhone ?? '').replace(/\D/g, '')
  if (!digitsOnly) {
    return null
  }

  const dialCodeDigits = String(country?.dial_code ?? '').replace(/\D/g, '')
  const candidates = [digitsOnly]
  if (digitsOnly.startsWith('00') && digitsOnly.length > 2) {
    candidates.push(digitsOnly.slice(2))
  }

  for (const candidateRaw of candidates) {
    let candidate = candidateRaw
    if (candidate.length === expectedLength) {
      return candidate
    }

    if (dialCodeDigits && candidate.startsWith(dialCodeDigits)) {
      const localNumber = candidate.slice(dialCodeDigits.length)
      if (localNumber.length === expectedLength) {
        return localNumber
      }
      candidate = localNumber
    }

    while (candidate.length > expectedLength && candidate.startsWith('0')) {
      candidate = candidate.slice(1)
    }

    if (candidate.length === expectedLength) {
      return candidate
    }
  }

  return null
}

const normalizeSignupUsername = (raw: string) => {
  const normalized = String(raw ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9._]+/g, '.')
    .replace(/[._]{2,}/g, '.')
    .replace(/^[._]+|[._]+$/g, '')
    .slice(0, 100)

  return normalized || null
}

const buildSignupUsername = (data: PublicSignupFormData) => {
  const explicit = normalizeSignupUsername(data.username)
  if (explicit) {
    return explicit
  }

  const emailLocalPart = data.email.includes('@') ? data.email.split('@')[0] : data.email
  const fromEmail = normalizeSignupUsername(emailLocalPart)
  if (fromEmail) {
    return fromEmail
  }

  const fromName = normalizeSignupUsername(`${data.first_name}.${data.last_name}`)
  if (fromName) {
    return fromName
  }

  return normalizeSignupUsername(data.phone_number)
}

export default function SignupPublicPage() {
  const navigate = useNavigate()
  const { serviceInstanceId } = useParams<{ serviceInstanceId: string }>()
  const selectedBackendServerUrl = useSelectedBackendServerUrl()
  const authTheme = useAuthTheme()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    control,
    setError,
    clearErrors,
    setValue,
    watch,
    formState: { errors, dirtyFields },
  } = useForm<PublicSignupFormData>({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      username: '',
      phone_number: '',
      country_id: '',
      state_id: '',
      date_of_birth: '',
      age: '',
      gender: '',
      password: '',
      password_confirm: '',
      acknowledge_exposure_notice: false,
    },
  })

  const themeServiceInstanceId = authTheme.locked ? getAuthThemeServiceInstanceId(authTheme) : ''
  const effectiveServiceInstanceId = themeServiceInstanceId || serviceInstanceId || ''

  const organizationQuery = useQuery({
    queryKey: ['public-signup-service-instance', selectedBackendServerUrl, effectiveServiceInstanceId],
    queryFn: () => orgTreeApi.resolvePublicSignupServiceInstance(effectiveServiceInstanceId),
    enabled: Boolean(effectiveServiceInstanceId),
    staleTime: 60_000,
  })

  useRefetchOnReconnect({
    enabled: Boolean(effectiveServiceInstanceId) && organizationQuery.isError,
    refetch: organizationQuery.refetch,
  })

  useEffect(() => {
    if (!authTheme.locked || !themeServiceInstanceId || serviceInstanceId === themeServiceInstanceId) {
      return
    }

    navigate(`/signup/${encodeURIComponent(themeServiceInstanceId)}`, { replace: true })
  }, [authTheme.locked, navigate, serviceInstanceId, themeServiceInstanceId])

  const organization = (authTheme.locked ? authTheme.organization : null) ?? organizationQuery.data
  const selectedCountryId = watch('country_id')
  const selectedStateId = watch('state_id')
  const selectedGender = watch('gender')
  const shouldCollectFirstName = Boolean(organization?.signup_public_fname)
  const shouldCollectLastName = Boolean(organization?.signup_public_lname)
  const shouldCollectEmail = Boolean(organization?.signup_public_email)
  const shouldCollectMobile = Boolean(organization?.signup_public_mobile)
  const shouldCollectCountry = Boolean(organization?.signup_public_country) && organization?.signup_default_user_nativity !== 'home'
  const isHomeNativity = organization?.signup_default_user_nativity === 'home' || organization?.signup_default_user_nativity == null
  const shouldCollectState = Boolean(organization?.signup_public_state)
  const shouldCollectDob = Boolean(organization?.signup_public_dob)
  const shouldCollectAge = Boolean(organization?.signup_public_age)
  const shouldCollectGender = Boolean(organization?.signup_public_gender)
  const requiresCountryValue = shouldCollectCountry || shouldCollectMobile || isHomeNativity
  const organizationName = resolveOrganizationName(organization)
  const { sidePanelLogos, welcomeLogos } = resolveServiceInstanceAuthLogos(organization)
  const primaryColor = organization?.primary_color || authTheme.primaryColor
  const termsText = organization?.terms_and_conditions ?? null
  const privacyText = organization?.privacy_policy ?? null
  const resolvedServiceInstanceId = resolveServiceInstanceReference(organization, effectiveServiceInstanceId)

  const countriesQuery = useQuery({
    queryKey: ['org-tree-countries', selectedBackendServerUrl],
    queryFn: orgTreeApi.listCountries,
    enabled: requiresCountryValue,
    staleTime: 60_000,
  })

  const effectiveCountryId = selectedCountryId || (requiresCountryValue ? String(organization?.country_id || '') : '')
  const selectedCountry = countriesQuery.data?.find((country) => String(country.id) === String(effectiveCountryId))
  const phoneNumberHint = (() => {
    if (!shouldCollectMobile) {
      return null
    }

    const mask = String(selectedCountry?.phone_number_mask ?? '').trim()
    const rawDialCode = String(selectedCountry?.dial_code ?? '').trim()
    const dialCode = rawDialCode ? (rawDialCode.startsWith('+') ? rawDialCode : `+${rawDialCode}`) : ''
    if (!mask || !mask.includes('#')) {
      return dialCode
        ? `Enter your local mobile number only (without ${dialCode}).`
        : 'Enter your local mobile number only (without country code).'
    }

    const expectedLength = (mask.match(/#/g) ?? []).length
    const exampleMask = mask.replace(/#/g, '0')
    return dialCode
      ? `Expected local format: ${exampleMask} (${expectedLength} digits). Do not include ${dialCode}.`
      : `Expected local format: ${exampleMask} (${expectedLength} digits).`
  })()

  const statesQuery = useQuery({
    queryKey: ['org-tree-states', selectedBackendServerUrl, effectiveCountryId],
    queryFn: () => orgTreeApi.listStates(effectiveCountryId),
    enabled: shouldCollectState && Boolean(effectiveCountryId),
    staleTime: 60_000,
  })

  useEffect(() => {
    if (!requiresCountryValue) {
      return
    }
    // Don't override if the user has manually chosen a country.
    if (dirtyFields.country_id) {
      return
    }

    // Prefer the org's configured country; only fall back to the first list item if org hasn't set one.
    const preferredCountryId = organization?.country_id
      ? String(organization.country_id)
      : countriesQuery.data?.[0]?.id != null
        ? String(countriesQuery.data[0].id)
        : null
    if (!preferredCountryId || selectedCountryId === preferredCountryId) {
      return
    }

    setValue('country_id', preferredCountryId, { shouldValidate: false, shouldDirty: false })
  }, [countriesQuery.data, dirtyFields.country_id, organization?.country_id, requiresCountryValue, selectedCountryId, setValue])

  useEffect(() => {
    if (!shouldCollectState || !statesQuery.data?.length || selectedStateId) {
      return
    }

    const fallbackStateId = organization?.state_id || statesQuery.data[0]?.id
    if (!fallbackStateId) {
      return
    }

    setValue('state_id', String(fallbackStateId), { shouldValidate: false, shouldDirty: false })
  }, [organization?.state_id, selectedStateId, setValue, shouldCollectState, statesQuery.data])

  const loginParams = new URLSearchParams()
  if (organization?.identifier) {
    loginParams.set('tenant', organization.identifier)
  } else if (resolvedServiceInstanceId) {
    loginParams.set('tenant', resolvedServiceInstanceId)
  }
  const loginHref = loginParams.toString() ? `/login?${loginParams.toString()}` : '/login'
  const vendorSignupHref = resolvedServiceInstanceId
    ? `/signup/vendor/${encodeURIComponent(resolvedServiceInstanceId)}`
    : '/signup/vendor'
  const canShowOrganizationSignup = isRootOrgServiceInstance(organization)

  const validateForm = (data: PublicSignupFormData) => {
    clearErrors()

    let hasErrors = false

    const requireValue = (field: keyof PublicSignupFormData, value: string, message: string) => {
      if (value.trim()) {
        return
      }

      setError(field, { type: 'required', message })
      hasErrors = true
    }

    if (!organization?.signup_public_active) {
      toast.error('Signup is not enabled for this organization.')
      return false
    }

    if (!shouldCollectEmail && !shouldCollectMobile) {
      toast.error('This organization has no signup contact field configured.')
      return false
    }

    if (shouldCollectFirstName) {
      requireValue('first_name', data.first_name, 'First name is required')
    }

    if (shouldCollectLastName) {
      requireValue('last_name', data.last_name, 'Last name is required')
    }

    if (shouldCollectEmail) {
      requireValue('email', data.email, 'Email is required')
      if (data.email.trim() && !emailPattern.test(data.email.trim())) {
        setError('email', { type: 'pattern', message: 'Please enter a valid email address' })
        hasErrors = true
      }
    }

    if (shouldCollectMobile) {
      requireValue('phone_number', data.phone_number, 'Phone number is required')
      if (!effectiveCountryId) {
        setError('country_id', { type: 'required', message: 'Country is required for phone signup' })
        hasErrors = true
      } else if (!selectedCountry) {
        setError('country_id', {
          type: 'validate',
          message: 'Country details are still loading. Please wait and try again.',
        })
        hasErrors = true
      } else if (selectedCountry) {
        // Only enforce mask validation when the country has loaded; if still loading, skip and let backend validate.
        const normalizedPhone = normalizePhoneForCountry(data.phone_number, selectedCountry)
        if (!normalizedPhone) {
          const expectedDigits = (selectedCountry.phone_number_mask?.match(/#/g) ?? []).length
          const rawDial = selectedCountry.dial_code ? selectedCountry.dial_code.replace(/\D/g, '') : ''
          const dialCode = rawDial ? `+${rawDial}` : ''
          const hint = expectedDigits > 0
            ? `Enter ${expectedDigits} digits${dialCode ? ` (without ${dialCode})` : ''}.`
            : `Enter your local mobile number${dialCode ? ` without ${dialCode}` : ''}.`
          setError('phone_number', {
            type: 'validate',
            message: `Invalid phone number. ${hint}`,
          })
          hasErrors = true
        }
      }
    }

    if (shouldCollectCountry && !data.country_id.trim()) {
      setError('country_id', { type: 'required', message: 'Country is required' })
      hasErrors = true
    }

    if (shouldCollectState && !data.state_id.trim()) {
      setError('state_id', { type: 'required', message: 'State is required' })
      hasErrors = true
    }

    if (shouldCollectDob) {
      requireValue('date_of_birth', data.date_of_birth, 'Date of birth is required')
    }

    if (shouldCollectAge) {
      requireValue('age', data.age, 'Age is required')
    }

    if (shouldCollectGender) {
      requireValue('gender', data.gender, 'Gender is required')
    }

    if (data.password.length < 8) {
      setError('password', { type: 'minLength', message: 'Password must be at least 8 characters' })
      hasErrors = true
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(data.password)) {
      setError('password', { type: 'pattern', message: 'Password must contain uppercase, lowercase, and number' })
      hasErrors = true
    }

    if (!data.password_confirm.trim()) {
      setError('password_confirm', { type: 'required', message: 'Confirm password is required' })
      hasErrors = true
    } else if (data.password !== data.password_confirm) {
      setError('password_confirm', { type: 'validate', message: 'Passwords do not match' })
      hasErrors = true
    }

    if (!data.acknowledge_exposure_notice) {
      setError('acknowledge_exposure_notice', {
        type: 'required',
        message: 'Please acknowledge how your account is visible within the organization',
      })
      hasErrors = true
    }

    return !hasErrors
  }

  const onSubmit = async (data: PublicSignupFormData) => {
    if (!organization || !validateForm(data)) {
      return
    }

    const normalizedPhoneForSubmit = shouldCollectMobile
      ? (normalizePhoneForCountry(data.phone_number, selectedCountry) ?? undefined)
      : undefined

    if (shouldCollectMobile && !normalizedPhoneForSubmit) {
      const expectedDigits = (selectedCountry?.phone_number_mask?.match(/#/g) ?? []).length
      const rawDial = selectedCountry?.dial_code ? selectedCountry.dial_code.replace(/\D/g, '') : ''
      const dialCode = rawDial ? `+${rawDial}` : ''
      const hint = expectedDigits > 0
        ? `Enter ${expectedDigits} digits${dialCode ? ` (without ${dialCode})` : ''}.`
        : `Enter your local mobile number${dialCode ? ` without ${dialCode}` : ''}.`
      setError('phone_number', {
        type: 'validate',
        message: `Invalid phone number. ${hint}`,
      })
      return
    }

    const signupUsername = buildSignupUsername(data)
    if (!signupUsername) {
      setError('email', {
        type: 'validate',
        message: 'Unable to generate a valid username for signup. Please provide a valid email.',
      })
      return
    }

    setIsSubmitting(true)
    try {
      const response = await authApi.signup({
        signup_type: 'user',
        first_name: shouldCollectFirstName ? data.first_name.trim() : undefined,
        last_name: shouldCollectLastName ? data.last_name.trim() : undefined,
        email: shouldCollectEmail ? data.email.trim() : undefined,
        phone_number: shouldCollectMobile
          ? (normalizedPhoneForSubmit ?? undefined)
          : undefined,
        country_id: effectiveCountryId ? Number(effectiveCountryId) : undefined,
        state_id: shouldCollectState && data.state_id ? Number(data.state_id) : undefined,
        date_of_birth: shouldCollectDob ? data.date_of_birth : undefined,
        age: shouldCollectAge ? data.age.trim() : undefined,
        gender: shouldCollectGender ? data.gender : undefined,
        password: data.password,
        service_instance_id: resolvedServiceInstanceId,
        org_instance_id: resolvedServiceInstanceId,
        tenant: organization.identifier || resolvedServiceInstanceId,
        acknowledge_exposure_notice: data.acknowledge_exposure_notice,
      })

      const loginTenant = response.data.company_identifier || response.data.service_instance_id || resolvedServiceInstanceId
      const canAutoLogin = shouldCollectEmail && Boolean(data.email.trim())

      if (isSignupEmailOtpVerificationContext(response.data.context) && shouldCollectEmail && data.email.trim()) {
        navigate(buildSignupEmailVerificationPath({
          email: data.email.trim(),
          tenant: loginTenant,
          orgInstanceId: response.data.service_instance_id || resolvedServiceInstanceId,
          otpLength: response.data.otp_length,
        }), { replace: true })
        return
      }

      if (loginTenant && canAutoLogin) {
        try {
          const loginResponse = await authApi.login({
            tenant: loginTenant,
            email: data.email.trim(),
            password: data.password,
          })

          if (loginResponse.data?.access_token) {
            toast.success('Account created successfully! Select a post to continue.')
            navigate(buildPostSelectionPath(loginResponse.data.service_instance_id || loginTenant), { replace: true })
            return
          }
        } catch {
          // Signup succeeded; fall back to the sign-in redirect below.
        }
      }

      toast.success('Account created successfully! Please sign in.')
      const redirectParams = new URLSearchParams()
      if (loginTenant) {
        redirectParams.set('tenant', loginTenant)
      }
      navigate(redirectParams.toString() ? `/login?${redirectParams.toString()}` : '/login')
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (organizationQuery.isLoading) {
    return (
      <div
        className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-background to-cyan-50/30 px-4 py-10"
        style={buildAuthThemeStyle(primaryColor)}
      >
        <div aria-hidden className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-cyan-200/30 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-orange-200/30 blur-3xl" />
        <div className="mx-auto w-full max-w-md">
          <Card className="w-full max-w-md border-border/70 bg-card/95 shadow-xl shadow-slate-900/5 backdrop-blur">
          <CardContent className="flex items-center justify-center py-12">
            <div className="w-full space-y-4">
              <BackendServerSelector className="w-full" />
              <div className="flex items-center justify-center py-4">
                <Spinner className="mr-3" />
                <span className="text-sm text-muted-foreground">Loading organization signup page...</span>
              </div>
            </div>
          </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (!organization) {
    if (organizationQuery.isError) {
      return (
        <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-background to-cyan-50/30 px-4 py-10">
          <div aria-hidden className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-cyan-200/30 blur-3xl" />
          <div aria-hidden className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-orange-200/30 blur-3xl" />
          <div className="mx-auto w-full max-w-md">
            <Card className="w-full max-w-md border-border/70 bg-card/95 shadow-xl shadow-slate-900/5 backdrop-blur">
            <CardHeader>
              <CardTitle>Connection problem</CardTitle>
              <CardDescription>We could not load this signup page due to a temporary network or server issue.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <BackendServerSelector className="w-full" />
              <Button className="w-full" onClick={() => {
                void organizationQuery.refetch()
              }}>
                Retry loading this signup page
              </Button>
              <Button asChild variant="outline" className="w-full">
                <Link to="/signup">Try another identifier</Link>
              </Button>
            </CardContent>
            </Card>
          </div>
        </div>
      )
    }

    return (
      <div
        className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-background to-cyan-50/30 px-4 py-10"
        style={buildAuthThemeStyle(primaryColor)}
      >
        <div aria-hidden className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-cyan-200/30 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-orange-200/30 blur-3xl" />
        <div className="mx-auto w-full max-w-md">
          <Card className="w-full max-w-md border-border/70 bg-card/95 shadow-xl shadow-slate-900/5 backdrop-blur">
          <CardHeader>
            <CardTitle>Organization not found</CardTitle>
            <CardDescription>We could not load a signup configuration for this Organization ID or Service ID.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <BackendServerSelector className="w-full" />
            <Button asChild className="w-full">
              <Link to="/signup">Try another identifier</Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to="/login">Go to sign in</Link>
            </Button>
          </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (!organization.signup_public_active) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-background to-cyan-50/30 px-4 py-10">
        <div aria-hidden className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-cyan-200/30 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-orange-200/30 blur-3xl" />
        <div className="mx-auto w-full max-w-md">
          <Card className="w-full max-w-md border-border/70 bg-card/95 shadow-xl shadow-slate-900/5 backdrop-blur">
          <CardHeader>
            <CardTitle>{organizationName}</CardTitle>
            <CardDescription>Signup is currently disabled for this organization.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <BackendServerSelector className="w-full" />
            <Button asChild className="w-full">
              <Link to="/signup">Use another identifier</Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to={loginHref}>Sign in</Link>
            </Button>
          </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <TenantAuthShell
      organizationName={organizationName}
      sidePanelLogos={sidePanelLogos}
      welcomeLogos={welcomeLogos}
      primaryColor={primaryColor}
      icon={Briefcase}
      eyebrow=""
      title=""
      description=""
      panelTitle=""
      panelDescription=""
      contentMaxWidth="max-w-2xl"
      logoPosition="left"
      contentPanelClassName="rounded-[24px] bg-white/95 px-5 py-5 text-foreground shadow-xl shadow-slate-950/15 backdrop-blur sm:px-6 sm:py-6"
      sideActions={(
        <div className="space-y-3 text-sm">
          <p className="text-primary-foreground/80">Other signup flows</p>
          <div className="flex flex-wrap gap-2">
            <Link to={`/signup/${encodeURIComponent(resolvedServiceInstanceId)}`} className="inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 font-semibold text-primary shadow-sm transition hover:bg-white">
              <UserCircle2 className="h-4 w-4" />
              User
            </Link>
            <Link to={vendorSignupHref} className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 font-semibold text-white ring-1 ring-white/30 transition hover:bg-white/25">
              <Store className="h-4 w-4" />
              Vendor
            </Link>
            {canShowOrganizationSignup ? (
              <Link to="/signup/company" className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 font-semibold text-white ring-1 ring-white/30 transition hover:bg-white/25">
                <Building2 className="h-4 w-4" />
                Organization
              </Link>
            ) : null}
          </div>
        </div>
      )}
    >
        <Card className="border-0 bg-transparent shadow-none">
          <CardContent className="px-0">
            {!authTheme.locked ? (
              <div className="mb-4 text-right">
                <Button asChild variant="ghost" size="sm">
                  <Link to="/signup">
                    <ChevronLeft className="mr-2 h-4 w-4" />
                    Change organization
                  </Link>
                </Button>
              </div>
            ) : null}
            <BackendServerSelector disabled={isSubmitting} className="mb-4" />
            {!shouldCollectEmail && !shouldCollectMobile ? (
              <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                This organization is missing a signup contact field. Ask an administrator to enable email or mobile signup.
              </div>
            ) : null}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-2.5">
              {shouldCollectFirstName || shouldCollectLastName ? (
                <div className="grid gap-3 md:grid-cols-2">
                  {shouldCollectFirstName ? (
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First name</Label>
                      <Input id="first_name" disabled={isSubmitting} {...register('first_name')} aria-invalid={!!errors.first_name} />
                      {errors.first_name ? <p className="text-sm text-destructive">{errors.first_name.message}</p> : null}
                    </div>
                  ) : null}
                  {shouldCollectLastName ? (
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last name</Label>
                      <Input id="last_name" disabled={isSubmitting} {...register('last_name')} aria-invalid={!!errors.last_name} />
                      {errors.last_name ? <p className="text-sm text-destructive">{errors.last_name.message}</p> : null}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {shouldCollectEmail ? (
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" autoComplete="email" disabled={isSubmitting} {...register('email')} aria-invalid={!!errors.email} />
                  {errors.email ? <p className="text-sm text-destructive">{errors.email.message}</p> : null}
                </div>
              ) : null}

              {shouldCollectMobile ? (
                <div className="space-y-2">
                  <Label htmlFor="phone_number">Phone number</Label>
                  <Input id="phone_number" type="tel" placeholder={PHONE_NUMBER_SAMPLE_PLACEHOLDER} autoComplete="tel" disabled={isSubmitting} {...register('phone_number', { onChange: (e: React.ChangeEvent<HTMLInputElement>) => { e.target.value = e.target.value.replace(/\D/g, '') } })} aria-invalid={!!errors.phone_number} />
                  {phoneNumberHint ? <p className="text-xs text-muted-foreground">{phoneNumberHint}</p> : null}
                  {errors.phone_number ? <p className="text-sm text-destructive">{errors.phone_number.message}</p> : null}
                </div>
              ) : null}

              {shouldCollectCountry || shouldCollectState ? (
                <div className="grid gap-3 md:grid-cols-2">
                  {shouldCollectCountry ? (
                    <div className="space-y-2">
                      <Label htmlFor="country_id">Country</Label>
                      <input type="hidden" {...register('country_id')} />
                      <Select value={selectedCountryId || ''} onValueChange={(value) => setValue('country_id', value, { shouldValidate: true, shouldDirty: true })} disabled={isSubmitting || countriesQuery.isLoading || !countriesQuery.data?.length}>
                        <SelectTrigger id="country_id" aria-invalid={!!errors.country_id} className="w-full">
                          <SelectValue placeholder={countriesQuery.isLoading ? 'Loading countries...' : 'Select a country'} />
                        </SelectTrigger>
                        <SelectContent>
                          {(countriesQuery.data ?? []).map((country) => (
                            <SelectItem key={String(country.id)} value={String(country.id)}>
                              {[country.country_name, country.dial_code].filter(Boolean).join(' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.country_id ? <p className="text-sm text-destructive">{errors.country_id.message}</p> : null}
                    </div>
                  ) : null}

                  {shouldCollectState ? (
                    <div className="space-y-2">
                      <Label htmlFor="state_id">State</Label>
                      <input type="hidden" {...register('state_id')} />
                      <Select value={selectedStateId || ''} onValueChange={(value) => setValue('state_id', value, { shouldValidate: true, shouldDirty: true })} disabled={isSubmitting || statesQuery.isLoading || !statesQuery.data?.length}>
                        <SelectTrigger id="state_id" aria-invalid={!!errors.state_id} className="w-full">
                          <SelectValue placeholder={statesQuery.isLoading ? 'Loading states...' : 'Select a state'} />
                        </SelectTrigger>
                        <SelectContent>
                          {(statesQuery.data ?? []).map((state) => (
                            <SelectItem key={String(state.id)} value={String(state.id)}>
                              {state.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.state_id ? <p className="text-sm text-destructive">{errors.state_id.message}</p> : null}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {shouldCollectDob || shouldCollectAge || shouldCollectGender ? (
                <div className="grid gap-3 md:grid-cols-3">
                  {shouldCollectDob ? (
                    <div className="space-y-2">
                      <Label htmlFor="date_of_birth">Date of birth</Label>
                      <Input id="date_of_birth" type="date" disabled={isSubmitting} {...register('date_of_birth')} aria-invalid={!!errors.date_of_birth} />
                      {errors.date_of_birth ? <p className="text-sm text-destructive">{errors.date_of_birth.message}</p> : null}
                    </div>
                  ) : null}

                  {shouldCollectAge ? (
                    <div className="space-y-2">
                      <Label htmlFor="age">Age</Label>
                      <Input id="age" inputMode="numeric" disabled={isSubmitting} {...register('age')} aria-invalid={!!errors.age} />
                      {errors.age ? <p className="text-sm text-destructive">{errors.age.message}</p> : null}
                    </div>
                  ) : null}

                  {shouldCollectGender ? (
                    <div className="space-y-2">
                      <Label htmlFor="gender">Gender</Label>
                      <input type="hidden" {...register('gender')} />
                      <Select value={selectedGender || ''} onValueChange={(value) => setValue('gender', value, { shouldValidate: true, shouldDirty: true })} disabled={isSubmitting}>
                        <SelectTrigger id="gender" aria-invalid={!!errors.gender} className="w-full">
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          {genderOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.gender ? <p className="text-sm text-destructive">{errors.gender.message}</p> : null}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input id="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" disabled={isSubmitting} {...register('password')} aria-invalid={!!errors.password} />
                  <button type="button" onClick={() => setShowPassword((current) => !current)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password ? <p className="text-sm text-destructive">{errors.password.message}</p> : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password_confirm">Confirm password</Label>
                <div className="relative">
                  <Input id="password_confirm" type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password" disabled={isSubmitting} {...register('password_confirm')} aria-invalid={!!errors.password_confirm} />
                  <button type="button" onClick={() => setShowConfirmPassword((current) => !current)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password_confirm ? <p className="text-sm text-destructive">{errors.password_confirm.message}</p> : null}
              </div>

              <div className="rounded-lg border border-border p-3">
                <Controller
                  name="acknowledge_exposure_notice"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="acknowledge_exposure_notice"
                        checked={field.value}
                        onCheckedChange={(checked) => field.onChange(checked === true)}
                        aria-invalid={!!errors.acknowledge_exposure_notice}
                      />
                      <div className="space-y-1">
                        <Label htmlFor="acknowledge_exposure_notice" className="text-sm leading-5">
                          I understand how this account is visible within the organization.
                        </Label>
                        {errors.acknowledge_exposure_notice ? <p className="text-sm text-destructive">{errors.acknowledge_exposure_notice.message}</p> : null}
                      </div>
                    </div>
                  )}
                />
              </div>

              <AuthTermsDisclosure termsText={termsText} privacyText={privacyText} />

              <Button type="submit" className="w-full" disabled={isSubmitting || (!shouldCollectEmail && !shouldCollectMobile)}>
                {isSubmitting ? <Spinner className="mr-2" /> : null}
                Create account
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">Already have an account? </span>
              <Link to={loginHref} className="font-medium text-primary hover:underline">
                Sign in
              </Link>
              <div className="mt-3">
                <Link to={vendorSignupHref} className="inline-flex items-center gap-1 font-medium text-muted-foreground hover:text-primary">
                  Registering as a vendor? <span className="text-primary">Vendor signup</span>
                </Link>
              </div>
              {canShowOrganizationSignup ? (
                <div className="mt-2">
                  <Link to="/signup/company" className="inline-flex items-center gap-1 font-medium text-muted-foreground hover:text-primary">
                    Need to create an organization? <span className="text-primary">Organization signup</span>
                  </Link>
                </div>
              ) : null}
            </div>
          </CardContent>
        </Card>
    </TenantAuthShell>
  )
}